// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

/**
 * Dragging a range down a column of numbers, and what it adds up to.
 *
 * The thing a spreadsheet does that nothing else does: select some figures
 * and be told, immediately, what they come to. Not a calculation the document
 * keeps -- a question you asked for a second and then stopped asking.
 *
 * The estimate grid had this logic inline. The Decisions report needs the
 * same behaviour over a different list, and two hand-written copies of
 * "which rows are between these two, minus the ones ctrl-clicked out" would
 * have disagreed the first time either was touched.
 */

/**
 * The ids between `anchorId` and `headId` in `order`, minus `excluded`.
 *
 * Direction does not matter: a drag upward selects the same rows as the same
 * drag downward. An anchor that is not in `order` -- a row that has just been
 * filtered away, or deleted under the drag -- selects only itself rather than
 * collapsing to nothing, because losing the selection is more startling than
 * keeping a stale one.
 */
export function rangeIds(
  order: readonly string[],
  anchorId: string | null | undefined,
  headId?: string | null,
  excluded?: ReadonlySet<string>,
): string[] {
  if (!anchorId) return [];
  const a = order.indexOf(anchorId);
  if (a === -1) return [anchorId];
  const hRaw = headId ? order.indexOf(headId) : a;
  const h = hRaw < 0 ? a : hRaw;
  const lo = Math.min(a, h), hi = Math.max(a, h);
  const span = order.slice(lo, hi + 1);
  return excluded && excluded.size ? span.filter(id => !excluded.has(id)) : span;
}

export interface RangeStats {
  count: number;
  sum: number;
  /** Null for a single figure: the average of one number is that number, and
   *  saying so is noise. */
  average: number | null;
}

/** What the selection comes to. */
export function rangeStats(
  ids: readonly string[],
  amountOf: (id: string) => number | null | undefined,
): RangeStats {
  let count = 0, sum = 0;
  for (const id of ids) {
    const v = amountOf(id);
    if (v == null || !isFinite(v)) continue;
    count++;
    sum += v;
  }
  return { count, sum, average: count > 1 ? sum / count : null };
}
